import { useState } from 'react';

import { AddImageModal } from '@components/add-image/add-image';
import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { createForm, useCreateFormStore } from '@components/form';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconReplace } from '@components/icons/replace';
import { IconUrl } from '@components/icons/url';
import { useModal } from '@components/modal';
import { PortalCard } from '@components/portal-card';
import { forceHideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { useCreatePortalLink, usePortalLink, useUpdatePortalLink } from 'api/portals/hooks';

import { useAddToPortalSidebar } from './add-to-portal-sidebar';

export const useAddCustomLinkSidebar = () => {
  const sidebar = useSidebar(AddCustomLinkSidebar, {
    variant: 'REGULAR',
    closeOnClickOutside: true,
  });
  return sidebar;
};

type PortalLink = {
  title: string;
  description: string;
  siteURL: string;
};

const Form = createForm<PortalLink>();

export const AddCustomLinkSidebar = ({
  portalShortId,
  linkShortId = '',
}: {
  portalShortId: string;
  linkShortId?: string;
}) => {
  const { t } = useScopedTranslate(locale);

  const { data: existingLink, isLoading } = usePortalLink(portalShortId, linkShortId);
  const createMutation = useCreatePortalLink(portalShortId);
  const updateMutation = useUpdatePortalLink(portalShortId, linkShortId);
  const showModal = useModal(AddImageModal, { variant: 'LARGE' });
  const showAddToPortalSidebar = useAddToPortalSidebar();

  const [image, setImage] = useState<File | undefined>(undefined);
  const formStore = useCreateFormStore();
  const formValues = formStore.useFormValues();
  const linkTitle = formValues['title'] as string;
  const linkDescription = formValues['description'] as string;
  const isDirty = formStore.useIsDirty();
  const hasImageChanged = Boolean(image);

  const isEditMode = Boolean(linkShortId && linkShortId.trim() !== '');
  const portalImage = image ? URL.createObjectURL(image) : existingLink?.imageUrl;

  const canSave = isDirty || hasImageChanged;

  if (isEditMode && isLoading) {
    return <Sidebar.Spinner />;
  }

  const handleSubmit = async (data: PortalLink) => {
    const formData = new FormData();
    formData.append('name', data.title);
    formData.append('description', data.description);
    formData.append('link', data.siteURL);

    if (image) {
      formData.append('image', image);
    }

    if (isEditMode) {
      await updateMutation.mutateAsync(formData, {
        onSuccess: forceHideSidebar,
      });
    } else {
      await createMutation.mutateAsync(formData, {
        onSuccess: forceHideSidebar,
      });
    }
  };

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={() =>
          isEditMode ? (
            <IconUrl color="buttonIconPositive" />
          ) : (
            <IconArrowLeft
              color="buttonIconPositive"
              onClick={() => showAddToPortalSidebar({ portalShortId })}
              style={{ cursor: 'pointer' }}
            />
          )
        }
        iconColor="elementBlue100"
        title={isEditMode ? t('editCustomLink') : t('addCustomLink')}
      />
      <Sidebar.Content>
        <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSubmit}>
          <Stack space={6}>
            <Form.TextField
              id="title"
              label={t('linkTitle')}
              placeholder={t('titlePlaceholder')}
              defaultValue={existingLink?.name ?? ''}
              validators={['required']}
            />
            <Form.TextField
              id="description"
              label={t('description')}
              placeholder={t('descriptionPlaceholder')}
              defaultValue={existingLink?.description ?? ''}
            />
            <Form.TextField
              id="siteURL"
              label={t('siteURL')}
              placeholder={t('siteURLPlaceholder')}
              helpText={t('siteURLDescription')}
              validators={['url', 'required']}
              defaultValue={existingLink?.link ?? ''}
            />

            <Stack space={4} align="flex-start">
              <PortalCard
                name={linkTitle || existingLink?.name || t('titlePlaceholder')}
                description={
                  linkDescription || existingLink?.description || t('descriptionPlaceholder')
                }
                image={portalImage}
              />
              <ButtonGroup align="left">
                <Button
                  variant="secondary"
                  onClick={() => showModal({ onChange: setImage })}
                  text={t('replaceImage')}
                  iconLeft={<IconReplace />}
                />
              </ButtonGroup>
            </Stack>
          </Stack>
          <Sidebar.Footer>
            <Form.Actions>
              <Form.Reset text={t('core.cancel')} variant="secondary" onClick={forceHideSidebar} />
              <Form.Submit text={t('core.save')} variant="primary-blue" isDisabled={!canSave} />
            </Form.Actions>
          </Sidebar.Footer>
        </FormWithUnsavedChangesPrompt>
      </Sidebar.Content>
    </Sidebar>
  );
};

export const locale = {
  en: {
    addCustomLink: 'Add custom link',
    editCustomLink: 'Edit custom link',
    linkTitle: 'Link title',
    titlePlaceholder: 'Add title...',
    description: 'Description',
    descriptionPlaceholder: 'Add description...',
    siteURL: 'Site URL',
    siteURLPlaceholder: 'https://acme.com/link',
    siteURLDescription:
      'Enter the URL users will be redirected to when clicking this link in the portal',
    replaceImage: 'Replace image',
  },
};
